import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc, orderBy } from 'firebase/firestore';
import {
    RotateCcw, Filter, CheckCircle, XCircle, Clock, AlertCircle,
    Package, ChevronDown, ChevronUp, Image as ImageIcon, Loader,
    Search, RefreshCw, User, FileText
} from 'lucide-react';

const STATUS_TABS = [
    { key: 'all', label: '전체' },
    { key: 'requested', label: '요청됨' },
    { key: 'seller_approved', label: '승인' },
    { key: 'seller_rejected', label: '거절' },
    { key: 'completed', label: '완료' }
];

const STATUS_CONFIG = {
    requested: { label: '요청됨', color: '#f59e0b', bg: '#fef3c7' },
    seller_approved: { label: '승인', color: '#3b82f6', bg: '#dbeafe' },
    seller_rejected: { label: '거절', color: '#ef4444', bg: '#fef2f2' },
    completed: { label: '완료', color: '#10b981', bg: '#d1fae5' }
};

const TYPE_LABELS = {
    refund: '환불',
    return: '반품',
    exchange: '교환'
};

const REASON_LABELS = {
    defective: '불량',
    wrong_item: '오배송',
    change_of_mind: '단순변심',
    not_as_described: '상품불일치',
    other: '기타'
};

function RefundManagement({ userInfo }) {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [expandedId, setExpandedId] = useState(null);
    const [rejectReasonMap, setRejectReasonMap] = useState({});
    const [processingId, setProcessingId] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    const loadRequests = useCallback(async () => {
        if (!userInfo?.uid) return;
        setLoading(true);

        try {
            const requestsRef = collection(db, 'refundRequests');
            const requestsQuery = query(
                requestsRef,
                where('sellerId', '==', userInfo.uid),
                orderBy('createdAt', 'desc')
            );
            const snapshot = await getDocs(requestsQuery);
            const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setRequests(list);
        } catch (err) {
            console.error('환불 요청 로드 실패:', err);
        } finally {
            setLoading(false);
        }
    }, [userInfo]);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    const filteredRequests = activeTab === 'all'
        ? requests
        : requests.filter(r => r.status === activeTab);

    const handleApprove = async (request) => {
        if (!window.confirm('이 요청을 승인하시겠습니까?')) return;
        setProcessingId(request.id);

        try {
            const requestRef = doc(db, 'refundRequests', request.id);
            const newHistoryEntry = {
                status: 'seller_approved',
                timestamp: new Date(),
                message: '판매자가 요청을 승인했습니다'
            };
            await updateDoc(requestRef, {
                status: 'seller_approved',
                statusHistory: [...(request.statusHistory || []), newHistoryEntry],
                updatedAt: new Date()
            });

            setRequests(prev => prev.map(r =>
                r.id === request.id
                    ? { ...r, status: 'seller_approved', statusHistory: [...(r.statusHistory || []), newHistoryEntry] }
                    : r
            ));
        } catch (err) {
            console.error('승인 처리 실패:', err);
            alert('승인 처리에 실패했습니다.');
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (request) => {
        const reason = rejectReasonMap[request.id];
        if (!reason || !reason.trim()) {
            alert('거절 사유를 입력해주세요.');
            return;
        }

        if (!window.confirm('이 요청을 거절하시겠습니까?')) return;
        setProcessingId(request.id);

        try {
            const requestRef = doc(db, 'refundRequests', request.id);
            const newHistoryEntry = {
                status: 'seller_rejected',
                timestamp: new Date(),
                message: `판매자 거절: ${reason.trim()}`
            };
            await updateDoc(requestRef, {
                status: 'seller_rejected',
                rejectReason: reason.trim(),
                statusHistory: [...(request.statusHistory || []), newHistoryEntry],
                updatedAt: new Date()
            });

            setRequests(prev => prev.map(r =>
                r.id === request.id
                    ? { ...r, status: 'seller_rejected', rejectReason: reason.trim(), statusHistory: [...(r.statusHistory || []), newHistoryEntry] }
                    : r
            ));
            setRejectReasonMap(prev => ({ ...prev, [request.id]: '' }));
        } catch (err) {
            console.error('거절 처리 실패:', err);
            alert('거절 처리에 실패했습니다.');
        } finally {
            setProcessingId(null);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const config = STATUS_CONFIG[status] || { label: status, color: '#64748b', bg: '#f1f5f9' };
        return (
            <span style={{
                padding: '4px 10px',
                fontSize: '12px',
                fontWeight: '600',
                color: config.color,
                backgroundColor: config.bg,
                borderRadius: '6px'
            }}>
                {config.label}
            </span>
        );
    };

    const getTabCount = (tabKey) => {
        if (tabKey === 'all') return requests.length;
        return requests.filter(r => r.status === tabKey).length;
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p style={styles.loadingText}>환불/반품 요청을 불러오는 중...</p>
                <style>{`
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <RotateCcw size={28} color="#6366f1" />
                    <div>
                        <h1 style={styles.headerTitle}>환불/반품 관리</h1>
                        <p style={styles.headerSubtitle}>고객의 환불, 반품, 교환 요청을 관리합니다</p>
                    </div>
                </div>
                <button style={styles.refreshBtn} onClick={loadRequests}>
                    <RefreshCw size={18} />
                    <span>새로고침</span>
                </button>
            </div>

            {/* Stats */}
            <div style={styles.statsRow}>
                {[
                    { label: '전체 요청', value: requests.length, icon: FileText, color: '#6366f1' },
                    { label: '대기중', value: requests.filter(r => r.status === 'requested').length, icon: Clock, color: '#f59e0b' },
                    { label: '승인', value: requests.filter(r => r.status === 'seller_approved').length, icon: CheckCircle, color: '#3b82f6' },
                    { label: '완료', value: requests.filter(r => r.status === 'completed').length, icon: CheckCircle, color: '#10b981' }
                ].map((stat, idx) => (
                    <div key={idx} style={styles.statCard}>
                        <stat.icon size={20} color={stat.color} />
                        <div>
                            <p style={styles.statValue}>{stat.value}</p>
                            <p style={styles.statLabel}>{stat.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div style={styles.tabBar}>
                {STATUS_TABS.map(tab => (
                    <button
                        key={tab.key}
                        style={{
                            ...styles.tabBtn,
                            ...(activeTab === tab.key ? styles.tabBtnActive : {})
                        }}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                        <span style={{
                            ...styles.tabCount,
                            ...(activeTab === tab.key ? styles.tabCountActive : {})
                        }}>
                            {getTabCount(tab.key)}
                        </span>
                    </button>
                ))}
            </div>

            {/* Requests List */}
            {filteredRequests.length === 0 ? (
                <div style={styles.emptyContainer}>
                    <RotateCcw size={64} color="#e2e8f0" />
                    <p style={styles.emptyTitle}>
                        {activeTab === 'all' ? '아직 요청이 없습니다' : `${STATUS_TABS.find(t => t.key === activeTab)?.label} 상태의 요청이 없습니다`}
                    </p>
                </div>
            ) : (
                <div style={styles.requestsList}>
                    {filteredRequests.map((request) => {
                        const isExpanded = expandedId === request.id;
                        const isProcessing = processingId === request.id;

                        return (
                            <div key={request.id} style={styles.requestCard}>
                                {/* Card Header */}
                                <div
                                    style={styles.cardHeader}
                                    onClick={() => setExpandedId(isExpanded ? null : request.id)}
                                >
                                    <div style={styles.cardHeaderLeft}>
                                        <div style={styles.cardHeaderInfo}>
                                            <div style={styles.cardMeta}>
                                                {getStatusBadge(request.status)}
                                                <span style={styles.typeBadge}>
                                                    {TYPE_LABELS[request.type] || request.type}
                                                </span>
                                            </div>
                                            <p style={styles.cardOrderId}>
                                                주문번호: {request.orderId}
                                            </p>
                                            <p style={styles.cardDate}>
                                                {formatDate(request.createdAt)}
                                            </p>
                                        </div>
                                    </div>
                                    <div style={styles.cardHeaderRight}>
                                        <span style={styles.cardAmount}>
                                            {(request.amount || 0).toLocaleString()}원
                                        </span>
                                        {isExpanded ? <ChevronUp size={20} color="#94a3b8" /> : <ChevronDown size={20} color="#94a3b8" />}
                                    </div>
                                </div>

                                {/* Expanded Details */}
                                {isExpanded && (
                                    <div style={styles.cardBody}>
                                        {/* Buyer Info */}
                                        <div style={styles.detailRow}>
                                            <User size={16} color="#6366f1" />
                                            <span style={styles.detailLabel}>구매자</span>
                                            <span style={styles.detailValue}>{request.buyerName || request.buyerId}</span>
                                        </div>

                                        {/* Reason */}
                                        <div style={styles.detailRow}>
                                            <AlertCircle size={16} color="#f59e0b" />
                                            <span style={styles.detailLabel}>사유</span>
                                            <span style={styles.detailValue}>
                                                {REASON_LABELS[request.reasonCategory] || request.reasonCategory}
                                            </span>
                                        </div>

                                        {/* Description */}
                                        <div style={styles.descriptionBox}>
                                            <p style={styles.descriptionLabel}>상세 설명</p>
                                            <p style={styles.descriptionText}>{request.description}</p>
                                        </div>

                                        {/* Images */}
                                        {request.images && request.images.length > 0 && (
                                            <div style={styles.imagesSection}>
                                                <p style={styles.descriptionLabel}>첨부 이미지</p>
                                                <div style={styles.imagesGrid}>
                                                    {request.images.map((img, idx) => (
                                                        <img
                                                            key={idx}
                                                            src={img}
                                                            alt={`증빙 ${idx + 1}`}
                                                            style={styles.evidenceImage}
                                                            onClick={() => setSelectedImage(img)}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Reject Reason (if rejected) */}
                                        {request.status === 'seller_rejected' && request.rejectReason && (
                                            <div style={styles.rejectReasonDisplay}>
                                                <XCircle size={16} color="#ef4444" />
                                                <div>
                                                    <p style={styles.rejectReasonTitle}>거절 사유</p>
                                                    <p style={styles.rejectReasonText}>{request.rejectReason}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Status History */}
                                        {request.statusHistory && request.statusHistory.length > 0 && (
                                            <div style={styles.historySection}>
                                                <p style={styles.descriptionLabel}>처리 이력</p>
                                                {request.statusHistory.map((entry, idx) => (
                                                    <div key={idx} style={styles.historyEntry}>
                                                        <div style={styles.historyDot} />
                                                        <div>
                                                            <span style={styles.historyMessage}>{entry.message}</span>
                                                            <span style={styles.historyTime}>
                                                                {formatDate(entry.timestamp)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Action Buttons (only for requested status) */}
                                        {request.status === 'requested' && (
                                            <div style={styles.actionSection}>
                                                <div style={styles.actionDivider} />

                                                {/* Reject Reason Input */}
                                                <div style={styles.rejectInputGroup}>
                                                    <input
                                                        type="text"
                                                        placeholder="거절 시 사유를 입력해주세요"
                                                        value={rejectReasonMap[request.id] || ''}
                                                        onChange={(e) => setRejectReasonMap(prev => ({
                                                            ...prev,
                                                            [request.id]: e.target.value
                                                        }))}
                                                        style={styles.rejectInput}
                                                    />
                                                </div>

                                                <div style={styles.actionButtons}>
                                                    <button
                                                        style={{
                                                            ...styles.rejectBtn,
                                                            ...(isProcessing ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                                                        }}
                                                        onClick={() => handleReject(request)}
                                                        disabled={isProcessing}
                                                    >
                                                        {isProcessing ? (
                                                            <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                                        ) : (
                                                            <XCircle size={16} />
                                                        )}
                                                        <span>거절</span>
                                                    </button>
                                                    <button
                                                        style={{
                                                            ...styles.approveBtn,
                                                            ...(isProcessing ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                                                        }}
                                                        onClick={() => handleApprove(request)}
                                                        disabled={isProcessing}
                                                    >
                                                        {isProcessing ? (
                                                            <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                                        ) : (
                                                            <CheckCircle size={16} />
                                                        )}
                                                        <span>승인</span>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Full Image Modal */}
            {selectedImage && (
                <div style={styles.imageOverlay} onClick={() => setSelectedImage(null)}>
                    <img src={selectedImage} alt="증빙 이미지" style={styles.fullImage} />
                </div>
            )}

            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
}

const styles = {
    container: {
        maxWidth: '900px',
        margin: '0 auto',
        padding: '32px 24px'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '100px 20px'
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '3px solid #e2e8f0',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    loadingText: {
        marginTop: '16px',
        fontSize: '14px',
        color: '#64748b'
    },

    // Header
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '28px',
        flexWrap: 'wrap',
        gap: '16px'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    headerTitle: {
        margin: 0,
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a'
    },
    headerSubtitle: {
        margin: '4px 0 0 0',
        fontSize: '14px',
        color: '#64748b'
    },
    refreshBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 18px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        border: '1px solid #c7d2fe',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },

    // Stats
    statsRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '12px',
        marginBottom: '24px'
    },
    statCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    statValue: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0
    },
    statLabel: {
        fontSize: '12px',
        color: '#94a3b8',
        margin: 0
    },

    // Tabs
    tabBar: {
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        overflowX: 'auto',
        paddingBottom: '4px'
    },
    tabBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 18px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap'
    },
    tabBtnActive: {
        color: '#ffffff',
        backgroundColor: '#6366f1'
    },
    tabCount: {
        fontSize: '12px',
        fontWeight: '700',
        color: '#94a3b8',
        backgroundColor: '#e2e8f0',
        padding: '2px 7px',
        borderRadius: '10px',
        minWidth: '20px',
        textAlign: 'center'
    },
    tabCountActive: {
        color: '#6366f1',
        backgroundColor: 'rgba(255,255,255,0.3)'
    },

    // Empty
    emptyContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px'
    },
    emptyTitle: {
        marginTop: '16px',
        fontSize: '16px',
        color: '#94a3b8'
    },

    // Requests List
    requestsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    requestCard: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        border: '1px solid #f1f5f9'
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '18px 20px',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    cardHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1
    },
    cardHeaderInfo: {
        flex: 1
    },
    cardMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '6px',
        flexWrap: 'wrap'
    },
    typeBadge: {
        padding: '3px 8px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        borderRadius: '6px'
    },
    cardOrderId: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#0f172a',
        margin: '0 0 2px 0'
    },
    cardDate: {
        fontSize: '12px',
        color: '#94a3b8',
        margin: 0
    },
    cardHeaderRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexShrink: 0
    },
    cardAmount: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#0f172a'
    },

    // Card Body
    cardBody: {
        padding: '0 20px 20px 20px',
        borderTop: '1px solid #f1f5f9'
    },
    detailRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 0',
        borderBottom: '1px solid #f8fafc'
    },
    detailLabel: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        minWidth: '60px'
    },
    detailValue: {
        fontSize: '14px',
        color: '#0f172a',
        flex: 1
    },
    descriptionBox: {
        padding: '16px 0'
    },
    descriptionLabel: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        marginBottom: '8px'
    },
    descriptionText: {
        fontSize: '14px',
        color: '#475569',
        lineHeight: '1.7',
        whiteSpace: 'pre-wrap',
        margin: 0,
        padding: '12px 16px',
        backgroundColor: '#f8fafc',
        borderRadius: '10px'
    },
    imagesSection: {
        paddingBottom: '16px'
    },
    imagesGrid: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap'
    },
    evidenceImage: {
        width: '80px',
        height: '80px',
        objectFit: 'cover',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        cursor: 'pointer'
    },
    rejectReasonDisplay: {
        display: 'flex',
        gap: '10px',
        padding: '14px 16px',
        backgroundColor: '#fef2f2',
        borderRadius: '10px',
        border: '1px solid #fecaca',
        marginBottom: '16px'
    },
    rejectReasonTitle: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#ef4444',
        margin: '0 0 4px 0'
    },
    rejectReasonText: {
        fontSize: '14px',
        color: '#991b1b',
        margin: 0,
        lineHeight: '1.5'
    },
    historySection: {
        paddingBottom: '16px'
    },
    historyEntry: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '8px 0'
    },
    historyDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: '#6366f1',
        marginTop: '6px',
        flexShrink: 0
    },
    historyMessage: {
        display: 'block',
        fontSize: '13px',
        color: '#475569'
    },
    historyTime: {
        display: 'block',
        fontSize: '11px',
        color: '#94a3b8',
        marginTop: '2px'
    },

    // Actions
    actionSection: {
        paddingTop: '4px'
    },
    actionDivider: {
        height: '1px',
        backgroundColor: '#e2e8f0',
        marginBottom: '16px'
    },
    rejectInputGroup: {
        marginBottom: '12px'
    },
    rejectInput: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        outline: 'none',
        color: '#0f172a',
        backgroundColor: '#f8fafc',
        boxSizing: 'border-box'
    },
    actionButtons: {
        display: 'flex',
        gap: '10px'
    },
    approveBtn: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#ffffff',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    rejectBtn: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#ef4444',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },

    // Image Overlay
    imageOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        cursor: 'pointer',
        padding: '40px'
    },
    fullImage: {
        maxWidth: '90%',
        maxHeight: '90%',
        objectFit: 'contain',
        borderRadius: '8px'
    }
};

export default RefundManagement;
